import { humanize } from 'common/helpers/humanize';
import i18n from '../../i18n/config';

const LEGACY_KEY_MAP = {
  accelerometer: 'accelerometer',
  temperature: 'temperature',
  motion_detection: 'motionDetection',
  current: 'current',
  current_scalar: 'currentScalar',
  pressure: 'pressure',
  ultrasound: 'ultrasound',
  ultrasound_vector: 'ultrasoundVector',
  other: 'other',
  magnetometer: 'magnetometer',
  level_detection: 'levelDetection',
  voltmeter: 'voltmeter',
};

function toSlug(str) {
  return String(str)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function tryTranslate(t, i18nInstance, namespace, key, defaultValue) {
  if (!key) return null;
  const translationKey = `${namespace}.${key}`;
  if (i18nInstance && typeof i18nInstance.exists === 'function' && i18nInstance.exists(translationKey)) {
    return t(translationKey);
  }
  if (defaultValue !== undefined) {
    return t(translationKey, { defaultValue });
  }
  return null;
}

function resolveRawType(rawType) {
  if (!rawType) return '';
  if (typeof rawType === 'object') {
    return rawType.text || rawType.value || rawType.name || '';
  }
  return String(rawType);
}

/**
 * Localized sensor / device type label (e.g. "Ultrasound Sensor", "Level Detection Sensor").
 *
 * @param {function} t - i18next `t`
 * @param {object} [i18nInstance] - i18next instance for `exists` lookups
 * @param {string|{ text?: string, value?: string, name?: string }} rawType
 * @returns {string}
 */
export function getSensorTypeDisplayName(t, i18nInstance, rawType) {
  const text = resolveRawType(rawType);
  if (!text) return '';

  const tr = typeof t === 'function' ? t : i18n.t.bind(i18n);
  const i18nRef = i18nInstance || i18n;
  const slug = toSlug(text);

  const legacyKey = LEGACY_KEY_MAP[slug];
  if (legacyKey) {
    const legacyMatch = tryTranslate(
      tr,
      i18nRef,
      'devices.filterOptions.sensorType',
      legacyKey,
      text
    );
    if (legacyMatch) return legacyMatch;
  }

  const slugMatch = tryTranslate(tr, i18nRef, 'devices.filterOptions.sensorType', slug);
  if (slugMatch) return slugMatch;

  const modelTypeMatch = tryTranslate(
    tr,
    i18nRef,
    'sensorModels.form.sensorTypeOptions',
    slug,
    undefined
  );
  if (modelTypeMatch) return modelTypeMatch;

  if (slug.endsWith('_sensor')) {
    const baseSlug = slug.replace(/_sensor$/, '');
    const baseLegacyKey = LEGACY_KEY_MAP[baseSlug];
    if (baseLegacyKey) {
      const baseLegacyMatch = tryTranslate(
        tr,
        i18nRef,
        'devices.filterOptions.sensorType',
        baseLegacyKey,
        text
      );
      if (baseLegacyMatch) return baseLegacyMatch;
    }
    const baseModelMatch = tryTranslate(
      tr,
      i18nRef,
      'sensorModels.form.sensorTypeOptions',
      baseSlug,
      undefined
    );
    if (baseModelMatch) return baseModelMatch;
  }

  return text.includes('_') ? humanize(text) : text;
}
